import { ChangeDetectionStrategy, Component, inject, Input, signal } from '@angular/core';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { ObjectType } from '@pixacare/domain';
import { FlattenedSequence } from '@pixacare/dtos';
import { TuiAnimated } from '@taiga-ui/cdk';
import { TuiDialogContext, TuiLoader, TuiNotificationService } from '@taiga-ui/core';
import { TuiNotificationMiddle } from '@taiga-ui/kit';
import { POLYMORPHEUS_CONTEXT } from '@taiga-ui/polymorpheus';
import { finalize } from 'rxjs';
import { ShareHttpService } from 'src/app/services/http/share.http.service';
import { MessageWithReceivers } from 'src/app/shared/models/chat/message-with-receivers';
import { ShareComponent } from '../../chat/share/share.component';
import { SequencePreviewComponent } from '../sequence-preview/sequence-preview.component';

@Component({
  templateUrl: './sequence-share.component.html',
  imports: [
    ShareComponent,
    SequencePreviewComponent,
    TranslocoDirective,
    TuiAnimated,
    TuiLoader,
    TuiNotificationMiddle,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-1 flex-col' },
})
export class SequenceShareComponent {
  private readonly context = inject<
    TuiDialogContext<
      MessageWithReceivers,
      {
        sequence: FlattenedSequence;
      }
    >
  >(POLYMORPHEUS_CONTEXT);

  @Input() sequence: FlattenedSequence = this.context.data.sequence;

  readonly isLoading = signal(false);

  private readonly shareService = inject(ShareHttpService);
  private readonly alertService = inject(TuiNotificationService);
  private readonly translocoService = inject(TranslocoService);

  close(): void {
    this.context.completeWith(null);
  }

  confirm(newMessage: MessageWithReceivers): void {
    this.isLoading.set(true);
    this.shareService
      .shareObject({
        clientCode: this.sequence.clientCode,
        toMailAddresses: newMessage.receivers.toMailAddresses,
        toUserIds: newMessage.receivers.toUserIds,
        toChannelIds: newMessage.receivers.toChannelIds,
        objectId: this.sequence.sequenceInstance.id,
        objectType: ObjectType.SEQUENCE,
        withAdminPrivileges: false,
        message: newMessage.text,
        expiryInDays: 7,
      })
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: () =>
          this.context.completeWith({
            ...newMessage,
            attachment: {
              sequence: this.sequence,
            },
          }),
        error: () =>
          this.alertService
            .open(
              this.translocoService.translate(
                "Quelque chose s'est mal passé, merci de réessayer dans quelques instants.",
              ),
              {
                label: this.translocoService.translate('Oups !'),
                appearance: 'error',
              },
            )
            .subscribe(),
      });
  }
}
